import {
  Box, Typography, useTheme, Button, InputBase, MenuItem,
  TextField, Collapse,
} from "@mui/material";

import SearchIcon          from "@mui/icons-material/Search";
import AddIcon             from "@mui/icons-material/Add";
import FilterListIcon      from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon   from "@mui/icons-material/KeyboardArrowUp";
import EditOutlinedIcon    from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon   from "@mui/icons-material/DeleteOutline";
import ImageIcon           from "@mui/icons-material/Image";
import InventoryIcon       from "@mui/icons-material/Inventory";

import ResponsiveDialog    from "components/responsiveDialog";
import FixedSizeDialog     from "components/fixedSizeDialog";
import ItemForm            from "../forms/itemCreateForms/itemForm";
import ItemImageEditForm   from "../forms/itemCreateForms/itemImageEditForm";
import EditItemForm        from "../forms/itemCreateForms/editItemForm";
import { useItemList, useItemTypeList } from "api/hooks/inventory";
import { useSelector }     from "react-redux";
import { useState }        from "react";
import { requester }       from "api/pesabackend";
import { URL }             from "api/url";
import ReportTable, { RCell } from "components/Tables/ReportTable";

/* ── Action button ── */
const ActionBtn = ({ label, icon, onClick, color = "default" }) => {
  const map = {
    default: { color: "#888780", border: "rgba(136,135,128,0.3)", hover: "rgba(136,135,128,0.08)" },
    warning: { color: "#BA7517", border: "rgba(186,117,23,0.3)",  hover: "rgba(186,117,23,0.08)"  },
    danger:  { color: "#e24b4a", border: "rgba(226,75,74,0.3)",   hover: "rgba(226,75,74,0.08)"   },
    primary: { color: "#1D9E75", border: "rgba(29,158,117,0.3)",  hover: "rgba(29,158,117,0.08)"  },
  };
  const c = map[color] ?? map.default;
  return (
    <Box onClick={onClick} sx={{
      display: "inline-flex", alignItems: "center", gap: "4px",
      px: "8px", py: "4px", borderRadius: "6px",
      border: `1px solid ${c.border}`, cursor: "pointer",
      color: c.color, fontSize: "12px", fontWeight: 500,
      transition: "all 0.15s",
      "&:hover": { backgroundColor: c.hover },
    }}>
      {icon && <Box sx={{ fontSize: "14px", display: "flex" }}>{icon}</Box>}
      {label}
    </Box>
  );
};

/* ── Field label+value pair ── */
const DetailField = ({ label, value, textPri, textSec }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
    <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: textSec }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "13px", color: textPri }}>{value || "—"}</Typography>
  </Box>
);

/* ── Expanded detail panel ── */
function DetailPanel({ row, theme, setNewItemData, setIsSuccess, accent, textPri, textSec, border, cardBg, isDark }) {
  const [open,          setOpen]          = useState(false);
  const [itemData,      setItemData]      = useState(null);
  const [openEditImage, setOpenEditImage] = useState(false);

  const toggle = async () => {
    if (!open && !itemData) {
      const res = await requester.get(`/inventory/products/get/${row.id}`);
      setItemData(res.data.data);
    }
    setOpen(!open);
  };

  return (
    <>
      {/* ── Main row ── */}
      <tr
        style={{
          borderBottom: `1px solid ${border}`,
          transition: "background 0.1s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? "rgba(29,158,117,0.07)" : "rgba(29,158,117,0.04)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
      >
        {/* Expand toggle */}
        <td style={{ padding: "10px 8px", width: "36px" }}>
          <Box
            onClick={toggle}
            sx={{
              width: "24px", height: "24px", borderRadius: "6px",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: textSec,
              border: `1px solid ${border}`,
              "&:hover": { borderColor: accent, color: accent },
              transition: "all 0.15s",
            }}
          >
            {open
              ? <KeyboardArrowUpIcon sx={{ fontSize: "16px" }} />
              : <KeyboardArrowDownIcon sx={{ fontSize: "16px" }} />}
          </Box>
        </td>

        {/* Image */}
        <td style={{ padding: "10px 8px", width: "56px" }}>
          {row.picture ? (
            <Box sx={{
              width: "44px", height: "44px", borderRadius: "8px",
              overflow: "hidden", border: `1px solid ${border}`, flexShrink: 0,
            }}>
              <img
                alt={row.product_name}
                src={`${URL}${row.picture}`}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          ) : (
            <Box sx={{
              width: "44px", height: "44px", borderRadius: "8px",
              backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#f5f5f5",
              border: `1px solid ${border}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: textSec,
            }}>
              <ImageIcon sx={{ fontSize: "18px", opacity: 0.4 }} />
            </Box>
          )}
        </td>

        <RCell>{row.product_name}</RCell>
        <RCell>{row.description}</RCell>
        <RCell>{row.related_product_category.category_name}</RCell>
        <RCell align="center">{row.reorder_level}</RCell>
        <RCell align="center">{row.maximum_stock > 0 ? row.maximum_stock : "N/A"}</RCell>
        <RCell align="center">{row.expiry_limit}</RCell>

        {/* Actions */}
        <td style={{ padding: "10px 12px", textAlign: "center" }}>
          <Box sx={{ display: "flex", gap: "4px", justifyContent: "center" }}>
            <ActionBtn
              label="Edit" icon={<EditOutlinedIcon fontSize="inherit" />} color="warning"
              onClick={() => document.dispatchEvent(new CustomEvent("item-edit", { detail: row.id }))}
            />
            <ActionBtn
              label="Delete" icon={<DeleteOutlineIcon fontSize="inherit" />} color="danger"
              onClick={() => document.dispatchEvent(new CustomEvent("item-delete", { detail: row.id }))}
            />
          </Box>
        </td>
      </tr>

      {/* ── Expanded row ── */}
      <tr>
        <td colSpan={9} style={{ padding: 0, border: "none" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            {itemData && (
              <Box sx={{
                m: "0 1rem 1rem",
                backgroundColor: isDark ? "rgba(29,158,117,0.04)" : "rgba(29,158,117,0.03)",
                border: `1px solid ${border}`,
                borderRadius: "10px",
                overflow: "hidden",
              }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0 }}>

                  {/* Image + edit */}
                  <Box sx={{
                    width: "180px", flexShrink: 0,
                    display: "flex", flexDirection: "column",
                    alignItems: "center", gap: "0.75rem",
                    p: "1.25rem",
                    borderRight: `1px solid ${border}`,
                  }}>
                    {itemData.picture ? (
                      <Box sx={{ width: "120px", height: "120px", borderRadius: "10px", overflow: "hidden", border: `1px solid ${border}` }}>
                        <img src={`${URL}${itemData.picture}`} alt={itemData.product_name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      </Box>
                    ) : (
                      <Box sx={{
                        width: "120px", height: "120px", borderRadius: "10px",
                        border: `1px dashed ${border}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#fafafa",
                      }}>
                        <ImageIcon sx={{ fontSize: "32px", color: textSec, opacity: 0.3 }} />
                      </Box>
                    )}
                    <ActionBtn
                      label="Edit image" icon={<EditOutlinedIcon fontSize="inherit" />} color="primary"
                      onClick={() => setOpenEditImage(true)}
                    />
                  </Box>

                  {/* Core details */}
                  <Box sx={{ flex: "1 1 200px", p: "1.25rem", display: "flex", flexDirection: "column", gap: "0.875rem", borderRight: `1px solid ${border}` }}>
                    <Typography sx={{ fontSize: "15px", fontWeight: 500, color: textPri }}>{itemData.product_name}</Typography>
                    <Typography sx={{ fontSize: "13px", color: textSec }}>{itemData.description}</Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", mt: "0.25rem" }}>
                      <DetailField label="Category"      value={itemData.related_product_category.category_name}  textPri={textPri} textSec={textSec} />
                      <DetailField label="Sub-category"  value={itemData.related_product_sub_category?.sub_category_name} textPri={textPri} textSec={textSec} />
                      <DetailField label="Smallest unit" value={itemData.smallest_unit.related_business_product_unit.name} textPri={textPri} textSec={textSec} />
                      <DetailField label="Selling price" value={itemData.smallest_unit.unit_selling_price}        textPri={textPri} textSec={textSec} />
                      <DetailField label="Reorder level" value={itemData.reorder_level}                           textPri={textPri} textSec={textSec} />
                      <DetailField label="Expiry limit"  value={itemData.expiry_limit}                            textPri={textPri} textSec={textSec} />
                      <DetailField label="Max stock"     value={itemData.maximum_stock}                           textPri={textPri} textSec={textSec} />
                    </Box>
                  </Box>

                  {/* Units table */}
                  <Box sx={{ flex: "1 1 240px", p: "1.25rem" }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: accent, mb: "0.75rem" }}>
                      Other Units
                    </Typography>
                    {itemData.product_units_list.length === 0 ? (
                      <Typography sx={{ fontSize: "13px", color: textSec }}>No other units defined.</Typography>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            {["Unit", "Smallest units", "Price"].map((h) => (
                              <th key={h} style={{
                                padding: "6px 10px", fontSize: "10px", fontWeight: 600,
                                letterSpacing: "0.07em", textTransform: "uppercase",
                                color: accent, textAlign: "left",
                                backgroundColor: isDark ? "rgba(29,158,117,0.08)" : "rgba(29,158,117,0.06)",
                                borderBottom: `1px solid ${border}`,
                              }}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {itemData.product_units_list.map((u, i) => (
                            <tr key={u.id} style={{
                              backgroundColor: i % 2 === 0 ? "transparent" : (isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)"),
                            }}>
                              <td style={{ padding: "7px 10px", fontSize: "13px", color: textPri, borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}` }}>
                                {u.related_business_product_unit.name}
                              </td>
                              <td style={{ padding: "7px 10px", fontSize: "13px", color: textPri, textAlign: "center", borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}` }}>
                                {u.number_of_smallest_units}
                              </td>
                              <td style={{ padding: "7px 10px", fontSize: "13px", color: textPri, borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}` }}>
                                {u.unit_selling_price}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Collapse>
        </td>
      </tr>

      {/* Edit image dialog */}
      <FixedSizeDialog
        dialogHeading="Edit Item Image"
        open={openEditImage}
        handleClickOpen={() => setOpenEditImage(true)}
        handleClose={() => setOpenEditImage(false)}
        negativeResponse="Cancel"
        maxWidth="md"
        content={
          <Box p="1rem" borderRadius="0.5rem" backgroundColor={theme.palette.background.alt}>
            <ItemImageEditForm
              itemId={row.id}
              setNewItemData={setNewItemData}
              setIsSuccess={setIsSuccess}
              setOpen={setOpen}
            />
          </Box>
        }
      />
    </>
  );
}

/* ── Main component ── */
export default function Items({ setIsSuccess }) {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const { productList, isLoadingItemList, setNewItemData, setSearchItem, setItemTypeFilter } =
    useItemList(1, business_branch_id);
  const { typeList } = useItemTypeList(1, business_branch_id);

  const [openCreate,     setOpenCreate]     = useState(false);
  const [openEdit,       setOpenEdit]       = useState(false);
  const [openDelete,     setOpenDelete]     = useState(false);
  const [editData,       setEditData]       = useState("");
  const [clickedItem,    setClickedItem]    = useState("");
  const [searchContent,  setSearchContent]  = useState("");

  /* listen for row-level edit/delete events */
  useState(() => {
    const onEdit = async (e) => {
      const id = e.detail;
      setClickedItem(id);
      const res = await requester.get(`/inventory/products/get_edit/${id}`);
      setEditData(res.data.data);
      setOpenEdit(true);
    };
    const onDelete = async (e) => {
      const id = e.detail;
      setClickedItem(id);
      await requester.get(`/inventory/products/get/${id}`);
      setOpenDelete(true);
    };
    document.addEventListener("item-edit",   onEdit);
    document.addEventListener("item-delete", onDelete);
    return () => {
      document.removeEventListener("item-edit",   onEdit);
      document.removeEventListener("item-delete", onDelete);
    };
  }, []);

  const handleClickDelete = async () => {
    await requester.post(`/inventory/products/delete/${clickedItem}`);
    setIsSuccess(true);
    setNewItemData(true);
    setOpenDelete(false);
  };

  const inputSx = {
    "& .MuiInputBase-root": { fontSize: "13px", height: "36px", backgroundColor: cardBg, borderRadius: "8px" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
    "& .MuiInputLabel-root": { fontSize: "12px" },
  };

  const columns = [
    { label: "" },          // expand toggle
    { label: "Image" },
    { label: "Item Name" },
    { label: "Description" },
    { label: "Category" },
    { label: "Reorder Level", align: "center" },
    { label: "Max Stock",     align: "center" },
    { label: "Expiry Limit",  align: "center" },
    { label: "Actions",       align: "center" },
  ];

  const sharedDetailProps = { theme, setNewItemData, setIsSuccess, accent, textPri, textSec, border, cardBg, isDark };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{
            width: "40px", height: "40px", borderRadius: "10px",
            backgroundColor: "rgba(29,158,117,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: accent, flexShrink: 0,
          }}>
            <InventoryIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
              Item List
            </Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>
              {productList.length} items in catalogue
            </Typography>
          </Box>
        </Box>

        <Button
          onClick={() => setOpenCreate(true)}
          startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
          sx={{
            fontSize: "13px", fontWeight: 500,
            px: "1rem", py: "0.45rem", borderRadius: "8px",
            backgroundColor: accent, color: "#fff", textTransform: "none",
            "&:hover": { backgroundColor: "#0F6E56" },
          }}
        >
          Add Item
        </Button>
      </Box>

      {/* ── Filter bar ── */}
      <Box sx={{
        display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.75rem",
        p: "0.75rem 1rem",
        backgroundColor: cardBg,
        border: `1px solid ${border}`,
        borderRadius: "10px",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", color: accent }}>
          <FilterListIcon sx={{ fontSize: "16px" }} />
          <Typography sx={{ fontSize: "11px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em" }}>
            Filters
          </Typography>
        </Box>

        {/* Search */}
        <Box sx={{
          display: "flex", alignItems: "center", gap: "6px",
          px: "0.75rem", height: "36px",
          backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#f9f9f9",
          border: `1px solid ${border}`,
          borderRadius: "8px", flex: "1 1 180px", maxWidth: "280px",
        }}>
          <SearchIcon sx={{ fontSize: "16px", color: textSec }} />
          <InputBase
            placeholder="Search item name…"
            value={searchContent}
            onChange={(e) => setSearchContent(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") setSearchItem(searchContent); }}
            sx={{ flex: 1, fontSize: "13px", color: textPri }}
          />
          <Box
            onClick={() => setSearchItem(searchContent)}
            sx={{ fontSize: "11px", fontWeight: 500, color: accent, cursor: "pointer", px: "4px", "&:hover": { textDecoration: "underline" } }}
          >
            Search
          </Box>
        </Box>

        {/* Category filter */}
        <TextField label="Category" size="small" select
          defaultValue="all"
          onChange={(e) => setItemTypeFilter(e.target.value)}
          sx={{ ...inputSx, width: "180px" }}>
          <MenuItem value="all">All categories</MenuItem>
          {typeList.map((row) => (
            <MenuItem key={row.id} value={row.id}>{row.category_name}</MenuItem>
          ))}
        </TextField>
      </Box>

      {/* ── Table ── */}
      <ReportTable
        columns={columns}
        rows={productList}
        loading={isLoadingItemList}
        emptyMessage="No items found"
        renderRow={(row) => (
          <DetailPanel key={row.id} row={row} {...sharedDetailProps} />
        )}
      />

      {/* ── Dialogs ── */}
      <ResponsiveDialog
        dialogHeading="Add a New Item"
        open={openCreate}
        handleClickOpen={() => setOpenCreate(true)}
        handleClose={() => setOpenCreate(false)}
        negativeResponse="Cancel"
        content={
          <Box p="1rem" borderRadius="0.5rem" backgroundColor={theme.palette.background.alt}>
            <ItemForm setOpen={setOpenCreate} setNewItemData={setNewItemData} setIsSuccess={setIsSuccess} />
          </Box>
        }
      />

      <ResponsiveDialog
        dialogHeading="Edit Item"
        open={openEdit}
        handleClose={() => setOpenEdit(false)}
        negativeResponse="Cancel"
        content={
          <Box p="1rem" borderRadius="0.5rem">
            <EditItemForm setOpen={setOpenEdit} setNewItemData={setNewItemData} id={clickedItem} editData={editData} setIsSuccess={setIsSuccess} />
          </Box>
        }
      />

      <ResponsiveDialog
        dialogHeading="Delete this item?"
        open={openDelete}
        handleClose={() => setOpenDelete(false)}
        negativeResponse="Cancel"
        content={
          <Box p="1rem" borderRadius="0.5rem">
            <Typography sx={{ fontSize: "13px", color: textSec, mb: "1rem" }}>
              This action cannot be undone.
            </Typography>
            <Button
              fullWidth variant="contained"
              onClick={handleClickDelete}
              sx={{
                backgroundColor: "#e24b4a", color: "#fff",
                textTransform: "none", borderRadius: "8px",
                "&:hover": { backgroundColor: "#c23a39" },
              }}
            >
              Yes, delete item
            </Button>
          </Box>
        }
      />
    </Box>
  );
}